import {
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';

import { API_URL } from '@constants';
import {
  Attendance,
  PermissionLevel,
  ResourceType,
  SubType,
  UserSubType,
  UserType,
} from '@models';
import { AppEventType, EventQueueService, UtilitiesService } from '@services';
import { ClickableDirective } from '@shared/directives/clickable.directive';

const SMALL_IMAGE_SIZES = ['xs', 'sm'];

@Component({
  selector: 'app-profile-image',
  imports: [ClickableDirective, TranslatePipe, NgbTooltipModule],
  template: `
    <div
      class="profile-image rounded-circle position-relative overflow-hidden z-1 d-flex align-items-center justify-content-center {{
        size()
      }}"
      [class.active]="isActive()"
      [class.pending]="attendance() === attendanceValues.PENDING"
      [class.present]="attendance() === attendanceValues.PRESENT"
      [class.absent]="attendance() === attendanceValues.ABSENT"
      [class.profile-image--external]="
        (subtype() === UserSubType.GUEST ||
          subtype() === UserSubType.ONE_TIME_GUEST) &&
        userType() !== ResourceType.COMMUNITY
      "
      [class.lvl0]="permission() && permission()! < PermissionLevel.SEC_VIEW"
      [class.lvl1]="permission() === PermissionLevel.SEC_VIEW"
      [class.lvl2]="permission() === PermissionLevel.SEC_PRINT"
      [class.lvl3]="permission() === PermissionLevel.ACCESS"
      [class.lvl3_5]="permission() === PermissionLevel.UPLOAD"
      [class.lvl4]="permission() === PermissionLevel.CONTRIBUTE"
      [class.lvl5]="permission() === PermissionLevel.MODIFY_AND_DELETE"
    >
      @if (userType() === ResourceType.COMMUNITY) {
        <div
          class="avatar-wrapper rounded-circle w-100 h-100 d-flex align-items-center justify-content-center"
          [class]="{
            bg_lvl0: permission() && permission()! < PermissionLevel.SEC_VIEW,
            bg_lvl1: permission() === PermissionLevel.SEC_VIEW,
            bg_lvl2: permission() === PermissionLevel.SEC_PRINT,
            bg_lvl3:
              permission() === PermissionLevel.ACCESS ||
              permission() === undefined,
            bg_lvl3_5: permission() === PermissionLevel.UPLOAD,
            bg_lvl4: permission() === PermissionLevel.CONTRIBUTE,
            bg_lvl5: permission() === PermissionLevel.MODIFY_AND_DELETE,
          }"
        >
          <div class="avatar-label">{{ 'WEB_FILTER_ALL' | translate }}</div>
        </div>
      }
      @if (userType() !== ResourceType.COMMUNITY) {
        <div
          class="avatar-wrapper rounded-circle w-100 h-100 d-flex align-items-center justify-content-center"
        >
          @if (!profileImage()) {
            @if (shortName() && userType() === ResourceType.USER) {
              <div class="avatar-label">
                {{ shortName() }}
              </div>
            }
            @if (
              !shortName() ||
              userType() === ResourceType.GROUP ||
              userType() === ResourceType.ROOM
            ) {
              <div
                class="avatar-icon h-100 d-flex align-items-center justify-content-center"
              >
                @if (userType() === ResourceType.USER) {
                  <span class="bi bi-person"></span>
                }
                @if (userType() === ResourceType.GROUP) {
                  <span class="bi bi-people"></span>
                }
                @if (userType() === ResourceType.ROOM) {
                  <span class="bi bi-megaphone"></span>
                }
              </div>
            }
          } @else {
            <div
              class="avatar-picture rounded-circle w-100 h-100"
              style="background-image:url('{{ profileImage() }}')"
            ></div>
          }
          @if (canEditImage()) {
            <div
              appClickable
              class="avatar-edit position-absolute top-0 end-0 bottom-0 start-0 d-flex align-items-center justify-content-center fs-6"
              [attr.aria-label]="
                (profileImage() ? 'WEB_ACTIONS_REMOVE' : 'WEB_ACTIONS_UPLOAD')
                  | translate
              "
              (click)="handleRemoveOrEditUserImage()"
            >
              @if (!profileImage() && !!onEditUserImage()) {
                <span
                  class="bi bi-camera text-white"
                  [ngbTooltip]="'WEB_ACTIONS_UPLOAD' | translate"
                  container="body"
                ></span>
              }
              @if (profileImage() && !!onRemoveUserImage()) {
                <span
                  class="bi bi-x-lg text-white"
                  [ngbTooltip]="'WEB_ACTIONS_REMOVE' | translate"
                  container="body"
                ></span>
              }
            </div>
          }
        </div>
      }
    </div>
  `,
  styleUrl: './profile-image.component.scss',
})
export class ProfileImageComponent {
  destroyRef = inject(DestroyRef);
  eventQueueService = inject(EventQueueService);
  utilitiesService = inject(UtilitiesService);

  userId = input<number>();
  userType = input.required<ResourceType>();
  size = input('md');
  attendance = input<number>();
  isActive = input<boolean>();
  subtype = input<SubType[UserType]>();
  shortName = input<string>();
  isImageDefined = input<boolean>();
  permission = input<Nullable<PermissionLevel>>();
  canEditImage = input<boolean>();
  onRemoveUserImage = input<() => void>();
  onEditUserImage = input<() => void>();

  profileImage = signal('');
  attendanceValues = Attendance;
  UserSubType = UserSubType;
  ResourceType = ResourceType;
  PermissionLevel = PermissionLevel;

  constructor() {
    this.eventQueueService
      .on(AppEventType.LOAD_PROFILE_IMAGE)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ payload }) => {
        if (payload.rid === this.userId()) {
          this.profileImage.set(payload.image ?? '');
        }
      });

    effect(() => {
      this.userId();
      this.userType();
      this.isImageDefined();
      this.size();

      untracked(() => {
        this.profileImage.set(this.getProfileImage());
      });
    });
  }

  handleRemoveOrEditUserImage = () => {
    if (this.profileImage() && this.onRemoveUserImage()) {
      this.onRemoveUserImage()!();
    } else if (!this.profileImage() && this.onEditUserImage()) {
      this.onEditUserImage()!();
    }
  };

  getProfileImage = () => {
    const userId = this.userId();

    if (
      !userId ||
      this.userType() !== ResourceType.USER ||
      !this.isImageDefined()
    ) {
      return '';
    }

    const version = this.utilitiesService.getUserImageVersion(userId);
    const params = [
      ...(SMALL_IMAGE_SIZES.includes(this.size()) ? ['small=true'] : []),
      ...(version ? [`v=${version}`] : []),
    ];

    return `${API_URL}/users/${userId}/image${
      params.length ? `?${params.join('&')}` : ''
    }`;
  };
}
